import { EnvironmentalType } from "enum/EnvironmentalType";
import { ISensorMeasuredData, MeasuredData } from "types/ISensorMeasuredData";
import { SensorMarker } from "components/SensorMarker/SensorMarker";
import { EnvironmentalVariables } from "enum/EnvironmentalVariables";
import arrowIcon from "assets/marker-icon.svg";
import dropletIcon from "assets/droplet-icon.svg";
import warningClock from "assets/warning-clock.svg";

interface SensorMarkerIconProps {
  sensorItem: ISensorMeasuredData;
  isSelected: boolean;
  tabStateActivity: EnvironmentalType;
  isSensorSelectionMode?: boolean;
}

const SensorMarkerIcon = ({
  sensorItem,
  isSelected,
  tabStateActivity,
  isSensorSelectionMode,
}: SensorMarkerIconProps) => {
  const getEnvironmentalData = (
    measuredData: MeasuredData[],
    directionVar: EnvironmentalVariables,
    valueVar: EnvironmentalVariables,
  ) => {
    const directionData = measuredData.find(
      (item) => item.environmental_variable === directionVar,
    );
    const variableData = measuredData.find(
      (item) =>
        item.environmental_variable.includes(valueVar) &&
        item.environmental_variable !== EnvironmentalVariables.WAVE_HEIGHT_MAX,
    );

    return { directionData, variableData };
  };

  const getMeasuredDataByActivityTab = (
    sensorItem?: ISensorMeasuredData,
    tabStateActivity?: EnvironmentalType,
  ) => {
    if (!sensorItem)
      return {
        directionData: undefined,
        variableData: undefined,
      };

    const { measuredData } = sensorItem;

    let environmentalData;

    switch (tabStateActivity) {
      case EnvironmentalType.WEATHER: {
        environmentalData = getEnvironmentalData(
          measuredData,
          EnvironmentalVariables.WIND_DIR,
          EnvironmentalVariables.WIND_VEL,
        );
        // Fallback to precipitation if no wind data available
        if (
          !environmentalData.directionData &&
          !environmentalData.variableData
        ) {
          const precipData = measuredData.find(
            (item) =>
              item.environmental_variable === EnvironmentalVariables.PRECIP,
          );
          if (precipData) {
            environmentalData = {
              directionData: undefined,
              variableData: precipData,
            };
          }
        }
        break;
      }
      case EnvironmentalType.PRECIPITATION: {
        const precipData = measuredData.find(
          (item) =>
            item.environmental_variable === EnvironmentalVariables.PRECIP,
        );
        if (precipData) {
          environmentalData = {
            directionData: undefined,
            variableData: precipData,
          };
        } else {
          // Fallback to wind if no precipitation data
          environmentalData = getEnvironmentalData(
            measuredData,
            EnvironmentalVariables.WIND_DIR,
            EnvironmentalVariables.WIND_VEL,
          );
        }
        break;
      }
      case EnvironmentalType.PRECIPITATION_ACCUMULATED: {
        const precipAccumData = measuredData.find(
          (item) =>
            item.environmental_variable ===
            EnvironmentalVariables.PRECIP_ACCUM_24H,
        );
        if (precipAccumData) {
          environmentalData = {
            directionData: undefined,
            variableData: precipAccumData,
          };
        } else {
          // Fallback to wind if no accumulated precipitation data
          environmentalData = getEnvironmentalData(
            measuredData,
            EnvironmentalVariables.WIND_DIR,
            EnvironmentalVariables.WIND_VEL,
          );
        }
        break;
      }
      case EnvironmentalType.WAVE:
        environmentalData = getEnvironmentalData(
          measuredData,
          EnvironmentalVariables.WAVE_DIR,
          EnvironmentalVariables.WAVE_HEIGHT,
        );
        break;
      case EnvironmentalType.WATER_FLOW:
        environmentalData =
          sensorItem.environmentalType === EnvironmentalType.CURRENT
            ? getEnvironmentalData(
                measuredData,
                EnvironmentalVariables.CURRENT_DIR,
                EnvironmentalVariables.CURRENT_VEL,
              )
            : getEnvironmentalData(
                measuredData,
                EnvironmentalVariables.CURRENT_DIR,
                EnvironmentalVariables.WATER_LEVEL,
              );
        break;
      default:
        environmentalData = getEnvironmentalData(
          measuredData,
          EnvironmentalVariables.WIND_DIR,
          EnvironmentalVariables.WIND_VEL,
        );
        break;
    }

    return environmentalData;
  };

  const { directionData, variableData } = getMeasuredDataByActivityTab(
    sensorItem,
    tabStateActivity,
  );

  const isPrecipVariable =
    variableData?.environmental_variable === EnvironmentalVariables.PRECIP ||
    variableData?.environmental_variable?.startsWith("precip_accum");

  let icon = isPrecipVariable
    ? dropletIcon
    : !!directionData
    ? arrowIcon
    : !!variableData
    ? undefined
    : warningClock;

  return (
    <SensorMarker
      degrees={directionData?.value ?? 0}
      value={variableData?.value ?? 0}
      defaultUnit={variableData?.units}
      environmentalVariable={variableData?.environmental_variable}
      isSelected={isSelected}
      markerIcon={icon}
      isSensorSelectionMode={isSensorSelectionMode}
    />
  );
};

export default SensorMarkerIcon;
